/**
 * A line diff of a draft against the page version it edits (Myers' algorithm, pure). Lines are
 * compared exactly; CRLF line ends count as LF. Past `LINE_CAP` lines on either side it gives up
 * (the review shows the rendered draft only).
 */

export const LINE_CAP = 5000;
/** Most changed lines compared; beyond it the review shows the rendered draft only. */
export const EDIT_CAP = 2000;

export interface DiffLine {
  kind: "same" | "add" | "del";
  text: string;
}

/** A shown line, or a collapsed run of unchanged lines. */
export type DiffRow = DiffLine | { kind: "skip"; lines: DiffLine[] };

const lines = (text: string) => {
  const out = text.replace(/\r\n?/g, "\n").split("\n");
  // A final newline ends the last line; it does not start an empty one.
  if (out.length > 1 && out.at(-1) === "") {
    out.pop();
  }
  return text === "" ? [] : out;
};

/** The edit script from `before` to `after`, or null when either side exceeds `cap` lines. */
function* diffSteps(
  before: string,
  after: string,
  cap = LINE_CAP,
  editCap = EDIT_CAP,
): Generator<void, DiffLine[] | null> {
  const a = lines(before);
  const b = lines(after);
  if (a.length > cap || b.length > cap) {
    return null;
  }
  const n = a.length;
  const m = b.length;
  const max = n + m;
  const offset = max + 1;
  const v: Int32Array = new Int32Array(2 * max + 3);
  const trace: Int32Array[] = [];
  let found = false;
  for (let d = 0; d <= max && !found; d++) {
    if (d > editCap) {
      return null;
    }
    yield;
    // Only diagonals -d..d matter at step d: memory stays O(D²), not O(D·(n+m)).
    trace.push(v.slice(offset - d, offset + d + 1));
    for (let k = -d; k <= d; k += 2) {
      let x =
        k === -d || (k !== d && (v[offset + k - 1] as number) < (v[offset + k + 1] as number))
          ? (v[offset + k + 1] as number)
          : (v[offset + k - 1] as number) + 1;
      let y = x - k;
      while (x < n && y < m && a[x] === b[y]) {
        x++;
        y++;
      }
      v[offset + k] = x;
      if (x >= n && y >= m) {
        found = true;
        break;
      }
    }
  }
  // Walk the trace back from (n, m) to build the script.
  const out: DiffLine[] = [];
  let x = n;
  let y = m;
  for (let d = trace.length - 1; d >= 0; d--) {
    const snapshot = trace[d] as Int32Array;
    const at = (k: number) => snapshot[k + d] as number;
    const k = x - y;
    const prevK = k === -d || (k !== d && at(k - 1) < at(k + 1)) ? k + 1 : k - 1;
    const prevX = d === 0 ? 0 : at(prevK);
    const prevY = prevX - prevK;
    while (x > prevX && y > prevY) {
      out.push({ kind: "same", text: a[x - 1] as string });
      x--;
      y--;
    }
    if (d > 0) {
      if (x === prevX) {
        out.push({ kind: "add", text: b[y - 1] as string });
      } else {
        out.push({ kind: "del", text: a[x - 1] as string });
      }
    }
    x = prevX;
    y = prevY;
  }
  return out.reverse();
}

/** Synchronous renderer helper; server callers use the cooperative merge below. */
export function lineDiff(
  before: string,
  after: string,
  cap = LINE_CAP,
  editCap = EDIT_CAP,
): DiffLine[] | null {
  const steps = diffSteps(before, after, cap, editCap);
  for (;;) {
    const step = steps.next();
    if (step.done) {
      return step.value;
    }
  }
}

async function asyncDiff(before: string, after: string): Promise<DiffLine[] | null> {
  const steps = diffSteps(before, after);
  let iterations = 0;
  for (;;) {
    const step = steps.next();
    if (step.done) {
      return step.value;
    }
    // Yield to concurrent HTTP requests between bounded chunks of Myers' search.
    if (++iterations % 16 === 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }
}

/** The server's line diff: the same algorithm, yielding between bounded search chunks. */
export const lineDiffAsync = (before: string, after: string): Promise<DiffLine[] | null> =>
  asyncDiff(before, after);

/** Lines added and removed by a diff (an edit's size, wiki-collaboration-ux D8). */
export function lineStats(diff: readonly DiffLine[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const line of diff) {
    if (line.kind === "add") {
      added++;
    } else if (line.kind === "del") {
      removed++;
    }
  }
  return { added, removed };
}

/**
 * Collapses unchanged runs longer than `2 * context` lines, keeping `context` lines around each
 * change (a diff with no change collapses entirely).
 */
export function collapse(diff: readonly DiffLine[], context = 2): DiffRow[] {
  const rows: DiffRow[] = [];
  let i = 0;
  while (i < diff.length) {
    if (diff[i]?.kind !== "same") {
      rows.push(diff[i] as DiffLine);
      i++;
      continue;
    }
    let j = i;
    while (j < diff.length && diff[j]?.kind === "same") {
      j++;
    }
    const run = diff.slice(i, j);
    const head = i === 0 ? 0 : context;
    const tail = j === diff.length ? 0 : context;
    if (run.length > head + tail + 1) {
      rows.push(...run.slice(0, head));
      rows.push({ kind: "skip", lines: run.slice(head, run.length - tail) });
      rows.push(...run.slice(run.length - tail));
    } else {
      rows.push(...run);
    }
    i = j;
  }
  return rows;
}

export type ThreeWayMerge =
  | { status: "merged"; markdown: string }
  | { status: "conflict" }
  | { status: "too_large" };

interface Hunk {
  start: number;
  end: number;
  replacement: string[];
}
function hunks(diff: DiffLine[]): Hunk[] {
  const result: Hunk[] = [];
  let position = 0;
  let current: Hunk | undefined;
  for (const line of diff) {
    if (line.kind === "same") {
      current = undefined;
      position++;
    } else {
      if (!current) {
        current = { start: position, end: position, replacement: [] };
        result.push(current);
      }
      if (line.kind === "del") {
        current.end = ++position;
      } else {
        current.replacement.push(line.text);
      }
    }
  }
  return result;
}

/** Conservative line merge: adjacent edits need a human, as do overlapping insertions. */
export function threeWayMerge(base: string, theirs: string, ours: string): ThreeWayMerge {
  const left = lineDiff(base, theirs);
  const right = lineDiff(base, ours);
  return mergeDiffs(base, theirs, ours, left, right);
}

/** Server merge: the same algorithm, yielding between bounded search chunks. */
export async function threeWayMergeAsync(
  base: string,
  theirs: string,
  ours: string,
): Promise<ThreeWayMerge> {
  const left = await asyncDiff(base, theirs);
  const right = await asyncDiff(base, ours);
  return mergeDiffs(base, theirs, ours, left, right);
}

function mergeDiffs(
  base: string,
  theirs: string,
  ours: string,
  left: DiffLine[] | null,
  right: DiffLine[] | null,
): ThreeWayMerge {
  if (!left || !right || [...left, ...right].filter((l) => l.kind !== "same").length > EDIT_CAP) {
    return { status: "too_large" };
  }
  const a = hunks(left);
  const b = hunks(right);
  // Both hunk lists are ordered: detect overlap in linear time, including adjacent edits.
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    const x = a[i] as Hunk;
    const y = b[j] as Hunk;
    if (x.start <= y.end && y.start <= x.end) {
      return { status: "conflict" };
    }
    if (x.end < y.start) {
      i++;
    } else {
      j++;
    }
  }
  const merged = lines(base);
  for (const h of [...a, ...b].sort((x, y) => y.start - x.start)) {
    merged.splice(h.start, h.end - h.start, ...h.replacement);
  }
  const newline = /\r?\n$/.test(theirs) || /\r?\n$/.test(ours);
  return { status: "merged", markdown: merged.join("\n") + (newline && merged.length ? "\n" : "") };
}

const HEADING_LINE = /^ {0,3}#{1,6}(?:\s|$)/;
const FENCE_OPEN = /^ {0,3}(`{3,}|~{3,})/;

/**
 * The sections an edit touches (wiki-collaboration-ux D9): the slug of the heading each added or
 * removed line falls under, in the version it belongs to (`after` for an added line, `before`
 * for a removed one), in page order, each once; a change before the first heading counts as none.
 * ATX headings are matched to `before`'s and `after`'s heading keys in order, outside code fences.
 */
export function changedSections(
  before: readonly { slug: string }[],
  after: readonly { slug: string }[],
  diff: readonly DiffLine[],
): string[] {
  const found = new Set<string>();
  // A fence closes on its own character, at least as long, with nothing after (CommonMark).
  const state: Record<"before" | "after", { at: number; fence: string | null }> = {
    before: { at: -1, fence: null },
    after: { at: -1, fence: null },
  };
  const step = (side: "before" | "after", text: string) => {
    const s = state[side];
    const marker = FENCE_OPEN.exec(text)?.[1];
    if (s.fence) {
      if (
        marker &&
        marker[0] === s.fence[0] &&
        marker.length >= s.fence.length &&
        !text.trim().slice(marker.length).trim()
      ) {
        s.fence = null;
      }
    } else if (marker) {
      s.fence = marker;
    } else if (HEADING_LINE.test(text)) {
      s.at++;
    }
  };
  for (const line of diff) {
    if (line.kind !== "add") {
      step("before", line.text);
    }
    if (line.kind !== "del") {
      step("after", line.text);
    }
    // A blank line changes no section's text.
    if (!line.text.trim()) {
      continue;
    }
    if (line.kind === "add") {
      const slug = after[state.after.at]?.slug;
      if (slug) {
        found.add(slug);
      }
    } else if (line.kind === "del") {
      const slug = before[state.before.at]?.slug;
      if (slug) {
        found.add(slug);
      }
    }
  }
  return [...found];
}
